// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { type PartCatalog, type PartCategory, useEvolvePost } from '@evolvemep/api-ts';
import { Button, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import type { WithRequiredNotNull } from 'helpers/helper-types';
import { isNotNil } from 'helpers/isNotNil';
import { ExistingCategorySelector } from 'modules/Shop/CatalogSetup/ExistingCategorySelector';
import type { CategoryPath } from 'modules/Shop/CatalogSetup/useCategoryPath';

type Props = {
  partCatalog: PartCatalog | null;
  categoryPath: CategoryPath;
  onCategoryCreated: (partCategory: PartCategory) => void;
};

const CreateCatalogCategoryForm = ({
  partCatalog,
  categoryPath,
  onCategoryCreated,
}: WithRequiredNotNull<Props, 'partCatalog'>) => {
  const { apiCall } = useEvolvePost('shop/partCatalog/category');
  const form = useForm<{ name: string; parentPartCategory: CategoryPath[number] | null }>({
    initialValues: { name: '', parentPartCategory: categoryPath[categoryPath.length - 1] ?? null },
    validate: { name: (v) => !v },
  });

  const onSubmit = async ({ name, parentPartCategory }: typeof form.values) => {
    await apiCall({
      name,
      partCatalogId: partCatalog.partCatalogId,
      parentPartCategoryId: parentPartCategory?.partCategoryId,
    }).then(onCategoryCreated);
  };

  useFormModal(form, onSubmit);

  return (
    <>
      <TextInput
        label="Category Name"
        required
        autoFocus
        data-autofocus
        maxLength={64}
        {...form.getInputProps('name')}
      />
      <ExistingCategorySelector
        clearable
        partCatalog={partCatalog}
        label="Parent Category"
        placeholder="No parent category"
        {...form.getInputProps('parentPartCategory')}
      />
    </>
  );
};

export const CreateCatalogCategoryModal = (props: Props) => {
  const [opened, setOpened] = useState(false);
  return (
    <>
      <AgGridStyleTooltip label="Add new category" withArrow>
        <Button size="compact-xs" onClick={() => setOpened(true)} variant="light">
          <EvolveIcon icon="Add" size="xs" />
        </Button>
      </AgGridStyleTooltip>
      {isNotNil(props.partCatalog) && (
        <FormModal
          title="New catalog category"
          opened={opened}
          onClose={() => setOpened(false)}
          confirmationText="Create"
          type="submit"
        >
          <CreateCatalogCategoryForm {...props} partCatalog={props.partCatalog} />
        </FormModal>
      )}
    </>
  );
};
